const Unit5Notes = `
<div class="space-y-8 animate-fadeIn">
    
    <div>
        <h2 class="text-2xl font-black text-blue-900 mb-3 border-b-2 border-slate-200 pb-2">Unit V: Toxicology & Chronopharmacology</h2>
        <p class="text-slate-700 leading-relaxed text-[14px]">
            This final unit deals with the adverse and toxic effects of chemicals on living systems (Toxicology) and how biological rhythms influence drug effects and pharmacokinetics (Chronopharmacology).
        </p>
    </div>

    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-blue-800 mb-4 border-b border-slate-100 pb-2">1. Principles of Toxicology</h3>
        
        <div class="mb-5">
            <h4 class="text-[16px] font-bold text-blue-700 mb-2">A. Basic Knowledge of Toxicity Types</h4>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div class="bg-slate-50 p-4 border border-slate-200 rounded-lg">
                    <h5 class="font-bold text-slate-800 text-[14px] mb-1">Acute Toxicity</h5>
                    <p class="text-[12px] text-slate-600">Toxic effects occurring rapidly after a <strong>single dose</strong> or multiple doses given within a short period (usually within 24 hours).</p>
                </div>
                <div class="bg-slate-50 p-4 border border-slate-200 rounded-lg">
                    <h5 class="font-bold text-slate-800 text-[14px] mb-1">Subacute Toxicity</h5>
                    <p class="text-[12px] text-slate-600">Toxicity resulting from <strong>repeated exposure</strong> to a chemical for a period of up to 1 month (approx. 28 days).</p>
                </div>
                <div class="bg-slate-50 p-4 border border-slate-200 rounded-lg">
                    <h5 class="font-bold text-slate-800 text-[14px] mb-1">Chronic Toxicity</h5>
                    <p class="text-[12px] text-slate-600">Toxicity resulting from prolonged, <strong>continuous exposure</strong> over a long period (more than 3 months to a lifetime).</p>
                </div>
            </div>
        </div>

        <div class="mb-5">
            <h4 class="text-[16px] font-bold text-blue-700 mb-2">B. Specific Toxicological Endpoints</h4>
            <ul class="list-disc pl-5 space-y-2 text-[13px] text-slate-700">
                <li><strong>Genotoxicity:</strong> The ability of a chemical to damage genetic information (DNA/RNA) within a cell, causing mutations.</li>
                <li><strong>Mutagenicity:</strong> The induction of permanent transmissible changes in the amount or structure of the genetic material. (All mutagens are genotoxic, but not all genotoxins are mutagens).</li>
                <li><strong>Carcinogenicity:</strong> The ability of a substance to induce cancer or increase its incidence. (e.g., Benzene, Asbestos).</li>
                <li><strong>Teratogenicity:</strong> The ability of a chemical/drug to cause structural or functional malformations in the developing fetus when given to a pregnant female. (e.g., <em>Thalidomide</em> causing phocomelia).</li>
            </ul>
        </div>
    </div>

    <div class="bg-red-50 p-6 border border-red-200 rounded-xl shadow-sm">
        <h3 class="text-[18px] font-black text-red-800 mb-3">2. General Principles of Treatment of Poisoning</h3>
        <p class="text-[13px] text-slate-700 mb-3">The management of any poisoning follows a strict systematic approach:</p>
        <ol class="list-decimal pl-5 space-y-2 text-[13px] text-slate-700 font-medium">
            <li><strong>Resuscitation and Stabilization (ABC):</strong> Maintain <strong>A</strong>irway, <strong>B</strong>reathing, and <strong>C</strong>irculation.</li>
            <li><strong>Prevention of further absorption:</strong>
                <ul class="list-disc pl-5 mt-1 space-y-1 font-normal text-slate-600">
                    <li>Removal of contaminated clothes, washing skin/eyes.</li>
                    <li>Emesis (using Syrup Ipecac - rarely used now).</li>
                    <li><strong>Gastric Lavage:</strong> Washing the stomach with saline (effective if done within 1-2 hours of ingestion).</li>
                    <li><strong>Activated Charcoal:</strong> Highly porous charcoal that adsorbs the poison in the gut, preventing its absorption.</li>
                </ul>
            </li>
            <li><strong>Enhancement of elimination:</strong> <em>Forced diuresis</em> (alkalinization of urine for acidic drugs like phenobarbitone), Hemodialysis, or Hemoperfusion.</li>
            <li><strong>Administration of Specific Antidotes:</strong> Agents that specifically counteract the poison.</li>
            <li><strong>Supportive Care:</strong> Managing seizures, arrhythmias, and maintaining body temperature.</li>
        </ol>
    </div>

    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-blue-800 mb-3">3. Management of Specific Poisonings</h3>
        
        <div class="overflow-x-auto rounded-lg border border-slate-200">
            <table class="min-w-full bg-white text-[13px] text-left">
                <thead class="bg-slate-100 text-slate-700 border-b border-slate-200">
                    <tr>
                        <th class="px-4 py-3 font-bold w-1/4">Poison / Drug</th>
                        <th class="px-4 py-3 font-bold w-1/3">Clinical Symptoms</th>
                        <th class="px-4 py-3 font-bold w-5/12">Management & Specific Antidote</th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 text-slate-600">
                    <tr>
                        <td class="px-4 py-3 font-bold text-blue-900">Barbiturates</td>
                        <td class="px-4 py-3">Severe CNS depression, respiratory depression, coma, hypothermia, hypotension.</td>
                        <td class="px-4 py-3">
                            Gastric lavage, Activated charcoal. <br>
                            <strong>Forced Alkaline Diuresis:</strong> IV Sodium Bicarbonate is given to alkalinize the urine, which traps the acidic barbiturate in the urine, enhancing its excretion. (No specific antidote).
                        </td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-blue-900">Morphine (Opioids)</td>
                        <td class="px-4 py-3">Classic Triad: <strong>Pin-point pupils, Respiratory depression, Coma.</strong> Cyanosis, severe hypotension.</td>
                        <td class="px-4 py-3">
                            Maintain airway and ventilation. <br>
                            <span class="bg-green-100 text-green-800 font-bold px-1 rounded">Antidote: Naloxone</span> (IV). It is a competitive opioid receptor antagonist.
                        </td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-blue-900">Organophosphorus Compounds (OPC)</td>
                        <td class="px-4 py-3">Muscarinic excess (DUMBBELS): <strong>D</strong>iarrhea, <strong>U</strong>rination, <strong>M</strong>iosis (pin-point pupil), <strong>B</strong>ronchospasm, <strong>B</strong>radycardia, <strong>E</strong>mesis, <strong>L</strong>acrimation, <strong>S</strong>alivation. Muscle twitching (nicotinic).</td>
                        <td class="px-4 py-3">
                            Wash skin with soap. Maintain airway. <br>
                            1. <span class="bg-green-100 text-green-800 font-bold px-1 rounded">Atropine:</span> Blocks muscarinic receptors (given until pupils dilate).<br>
                            2. <span class="bg-green-100 text-green-800 font-bold px-1 rounded">Pralidoxime (PAM):</span> Cholinesterase reactivator.
                        </td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-blue-900">Heavy Metals<br><span class="text-[11px] text-slate-500">(Lead, Mercury, Arsenic)</span></td>
                        <td class="px-4 py-3">
                            <em>Lead:</em> Anemia, blue line on gums, encephalopathy.<br>
                            <em>Mercury:</em> Tremors, stomatitis, renal failure.<br>
                            <em>Arsenic:</em> Rice-water stools, garlic breath.
                        </td>
                        <td class="px-4 py-3">
                            Treated with <strong>Chelating Agents</strong> (bind to metals and excrete them).<br>
                            <ul class="list-disc pl-4 mt-1 space-y-1">
                                <li><strong>Lead:</strong> Calcium disodium EDTA, Succimer.</li>
                                <li><strong>Mercury:</strong> Dimercaprol (BAL).</li>
                                <li><strong>Arsenic:</strong> Dimercaprol (BAL).</li>
                            </ul>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <div class="bg-indigo-50 p-6 border border-indigo-200 rounded-xl shadow-sm">
        <h3 class="text-[18px] font-black text-indigo-900 mb-3 flex items-center gap-2">
            <span class="text-xl">⏱️</span> 4. Chronopharmacology
        </h3>
        <p class="text-[13px] text-slate-700 mb-4 leading-relaxed">
            Chronopharmacology is the study of how the timing of drug administration according to the biological rhythms affects both the efficacy and the toxicity of the drug.
        </p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
                <h4 class="text-[15px] font-bold text-indigo-800 mb-1">A. Rhythms and Biological Clock</h4>
                <ul class="list-disc pl-5 space-y-2 text-[13px] text-slate-700">
                    <li><strong>Biological Rhythms:</strong> Periodic variations in biological functions. The most important is the <strong>Circadian Rhythm</strong> (approx. 24-hour cycle), like the sleep-wake cycle.</li>
                    <li><strong>Biological Clock:</strong> The master pacemaker controlling circadian rhythms in humans is the <strong>Suprachiasmatic Nucleus (SCN)</strong> located in the hypothalamus. It synchronizes the body using light signals and melatonin secretion.</li>
                </ul>
            </div>
            <div>
                <h4 class="text-[15px] font-bold text-indigo-800 mb-1">B. Significance in Chronotherapy</h4>
                <p class="text-[13px] text-slate-700 mb-2">Matching drug delivery with the body's natural rhythms:</p>
                <ul class="list-disc pl-5 space-y-2 text-[13px] text-slate-700">
                    <li><strong>Statins (e.g., Atorvastatin):</strong> Given at night/bedtime because cholesterol synthesis by the liver peaks during the night.</li>
                    <li><strong>Corticosteroids:</strong> Given in the early morning to mimic the body's natural peak cortisol release and prevent suppression of the HPA axis.</li>
                    <li><strong>Anti-asthmatics:</strong> Asthma attacks are most common late at night/early morning. Night-time dosing of long-acting bronchodilators is highly effective.</li>
                </ul>
            </div>
        </div>
    </div>

</div>
`;

export default Unit5Notes;